/**
 * OnboardingCarousel Component
 * Main container for onboarding flow with progress dots and skip button
 */

import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { OnboardingPage } from './OnboardingPage';
import { useOnboarding } from './useOnboarding';
import { ONBOARDING_PAGES } from './onboardingData';

export const OnboardingCarousel: React.FC = () => {
  const {
    currentPage,
    totalPages,
    goToNextPage,
    goToPrevPage,
    goToPage,
    skipOnboarding,
    swipeHandlers,
    hasCompletedOnboarding
  } = useOnboarding();

  // Don't render if onboarding is completed
  if (hasCompletedOnboarding) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-white z-50 overflow-hidden"
      {...swipeHandlers}
    >
      {/* Back Button - Visible on pages 2-6 at top left */}
      {currentPage > 0 && (
        <button
          onClick={goToPrevPage}
          className="absolute top-5 left-5 md:top-24 md:left-20 z-20 w-[36px] h-[36px] border border-[#e5e7eb] rounded-[8px] flex items-center justify-center bg-white hover:bg-gray-50 transition-colors duration-200"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 text-[#6b7280]" />
        </button>
      )}

      {/* Skip Button - Always visible at top right */}
      <button
        onClick={skipOnboarding}
        className="absolute top-5 right-5 md:top-24 md:right-20 z-20 w-[80px] h-[36px] border border-[#e5e7eb] rounded-[8px] flex items-center justify-center bg-white hover:bg-gray-50 transition-colors duration-200"
      >
        <span
          className="text-[15px] font-medium leading-[22.5px] text-[#6b7280]"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Skip
        </span>
      </button>

      {/* Pages Container */}
      <div className="relative w-full h-full">
        {ONBOARDING_PAGES.map((page, index) => (
          <OnboardingPage
            key={page.id}
            page={page}
            onNext={goToNextPage}
            isActive={currentPage === index}
          />
        ))}
      </div>

      {/* Progress Dots - Mobile only, positioned above content */}
      <div className="md:hidden absolute top-[calc(60%+16px)] left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex items-center gap-[8px]">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToPage(index)}
              className="w-[8px] h-[8px] rounded-full transition-all duration-300 hover:scale-125"
              style={{
                backgroundColor: currentPage === index ? '#14b8a6' : '#e5e7eb'
              }}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

